import { buildScenarioBalance, type MaterialRow } from "@/planner/scenario-balance";
import type { PopulationCounts, WorkforceNeed } from "@/provider/types";
import type { Planet, Scenario } from "@/schema/types";

/**
 * Turns "buy 69 DW a week" into "build two more FP on Montem".
 *
 * Dividing the shortfall by what one building makes would be wrong, because a
 * new building also employs workers and they eat and drink. Part of what it
 * produces goes straight back into feeding the people it brought. So this adds
 * buildings to a planet that already makes the material and recomputes the
 * whole scenario each time, stopping when the shortfall is covered.
 *
 * It only suggests a building the planet already has and a product it already
 * makes. Inventing a new product or a new building type means choosing a
 * recipe and a place, and that belongs to the person, not to a guess here.
 */

export type KnockOn = { ticker: string; extraPerWeek: number };

export type BuildOption = {
  planetId: string;
  planetName: string;
  buildingCode: string;
  /** How many more of that building. */
  add: number;
  /** False when even the most it was willing to add leaves a gap. */
  covers: boolean;
  /** Shortfall left after adding, per week. Zero when it covers. */
  remainingPerWeek: number;
  /** Other materials the new buildings leave worse off — their own ingredients, mostly. */
  knockOn: KnockOn[];
};

export type BuildSuggestion = {
  ticker: string;
  shortfallPerWeek: number;
  option: BuildOption | null;
  /** Why there is no option, when there is not. */
  reason: string | null;
};

/** More than this and the answer is "build a different kind of base", not a number. */
const MAX_ADDED = 40;
const EPSILON = 0.05;

function key(value: string): string {
  return value.trim().toUpperCase();
}

type Candidate = { planet: Planet; factoryId: string; buildingCode: string };

/**
 * Buildings that make this material and would make more with another one.
 * A line with its slots fixed by hand does not grow when a building is added,
 * so it cannot be the answer.
 */
function findCandidates(scenario: Scenario, ticker: string): { usable: Candidate[]; fixedSlotsOnly: boolean } {
  const usable: Candidate[] = [];
  let fixed = false;

  for (const planet of scenario.planets) {
    for (const row of planet.produced) {
      if (key(row.name) !== ticker || row.amount <= 0 || !row.factoryId) continue;
      const factory = planet.factories.find((entry) => entry.id === row.factoryId);
      if (!factory) continue;

      if (row.allocatedSlots !== null) {
        fixed = true;
        continue;
      }
      if (!usable.some((entry) => entry.planet.id === planet.id && entry.factoryId === factory.id)) {
        usable.push({ planet, factoryId: factory.id, buildingCode: factory.buildingCode });
      }
    }
  }

  return { usable, fixedSlotsOnly: usable.length === 0 && fixed };
}

function shortfallOf(
  scenario: Scenario,
  ticker: string,
  workforceByBuilding: Map<string, PopulationCounts>,
  needs: WorkforceNeed[],
) {
  const balance = buildScenarioBalance(scenario, workforceByBuilding, needs);
  const row = balance.materials.find((entry) => entry.ticker === ticker);
  return { balance, shortfall: row?.netShortfall ?? 0 };
}

function evaluate(
  scenario: Scenario,
  candidate: Candidate,
  ticker: string,
  baseline: MaterialRow[],
  startingShortfall: number,
  workforceByBuilding: Map<string, PopulationCounts>,
  needs: WorkforceNeed[],
): { option: BuildOption | null; helps: boolean } {
  const working = structuredClone(scenario);
  const planet = working.planets.find((entry) => entry.id === candidate.planet.id)!;
  const factory = planet.factories.find((entry) => entry.id === candidate.factoryId)!;
  const originalCount = factory.count;

  let last = { balance: null as ReturnType<typeof buildScenarioBalance> | null, shortfall: startingShortfall, added: 0 };

  for (let added = 1; added <= MAX_ADDED; added += 1) {
    factory.count = originalCount + added;
    const { balance, shortfall } = shortfallOf(working, ticker, workforceByBuilding, needs);
    last = { balance, shortfall, added };

    // The first building not helping at all means more will not either: its
    // workers eat as much as it makes, and no number of them changes that.
    if (added === 1 && shortfall >= startingShortfall - EPSILON) return { option: null, helps: false };

    if (shortfall <= EPSILON) break;
  }

  const beforeByTicker = new Map(baseline.map((row) => [row.ticker, row.netShortfall]));
  const knockOn = (last.balance?.materials ?? [])
    .filter((row) => row.ticker !== ticker)
    .map((row) => ({ ticker: row.ticker, extraPerWeek: row.netShortfall - (beforeByTicker.get(row.ticker) ?? 0) }))
    .filter((entry) => entry.extraPerWeek > 0.5)
    .sort((a, b) => b.extraPerWeek - a.extraPerWeek);

  return {
    helps: true,
    option: {
      planetId: candidate.planet.id,
      planetName: candidate.planet.name,
      buildingCode: candidate.buildingCode,
      add: last.added,
      covers: last.shortfall <= EPSILON,
      remainingPerWeek: last.shortfall <= EPSILON ? 0 : last.shortfall,
      knockOn,
    },
  };
}

export function suggestBuilds(
  scenario: Scenario,
  workforceByBuilding: Map<string, PopulationCounts>,
  needs: WorkforceNeed[],
): BuildSuggestion[] {
  const baseline = buildScenarioBalance(scenario, workforceByBuilding, needs);

  return baseline.materials
    .filter((row) => row.netShortfall > EPSILON)
    .map((row): BuildSuggestion => {
      const { usable, fixedSlotsOnly } = findCandidates(scenario, row.ticker);

      if (usable.length === 0) {
        return {
          ticker: row.ticker,
          shortfallPerWeek: row.netShortfall,
          option: null,
          reason: fixedSlotsOnly
            ? "Made on slots you set by hand, so another building would not add to it."
            : "No base makes this, so it has to be bought or given a producer.",
        };
      }

      let best: BuildOption | null = null;
      let anyHelped = false;

      for (const candidate of usable) {
        const { option, helps } = evaluate(
          scenario,
          candidate,
          row.ticker,
          baseline.materials,
          row.netShortfall,
          workforceByBuilding,
          needs,
        );
        anyHelped ||= helps;
        if (!option) continue;

        // A plan that covers beats one that does not; then fewer buildings.
        const better =
          !best ||
          (option.covers && !best.covers) ||
          (option.covers === best.covers && option.covers && option.add < best.add) ||
          (option.covers === best.covers && !option.covers && option.remainingPerWeek < best.remainingPerWeek);
        if (better) best = option;
      }

      return {
        ticker: row.ticker,
        shortfallPerWeek: row.netShortfall,
        option: best,
        reason: best
          ? null
          : anyHelped
            ? null
            : "More buildings would not close this: the workers each one brings use as much as it makes.",
      };
    });
}
